import { apiWithdrawalConfirm } from '@api/apiCall';
import AppliedReferral from '@atoms/AppliedReferral';
import BPTnc from '@atoms/BPTnc';
import BPTncCheckBox from '@atoms/BPTncCheckBox';
import BPTnCHeader from '@atoms/BPTnCHeader';
import WithdrawalAdminFee from '@atoms/WithdrawalAdminFee';
import WithdrawalAmount from '@atoms/WithdrawalAmount';
import WithdrawalBankDetails from '@atoms/WithdrawalBankDetails';
import WithdrawalReason from '@atoms/WithdrawalReason';
import WithdrawalTotal from '@atoms/WithdrawalTotal';
import { Button, Divider, Text, View } from '@components/primitives';
import { AntDesign } from '@expo/vector-icons';
import { useToast } from '@hooks';
import navigationConstants from '@navigation/navigationConstants';
import { useNavigation } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import {
  logPaymentSummarySuccessfull,
  logScreenBack,
  logScreenLaunch,
} from '@screens/GGWithdrawSuccess/analyticsWithdrawSuccess';
import { getStore } from '@store/storeUtils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import colors from '@theme/colors';
import { AMPLITUDE_CONSTANTS } from '@utils/analytics/constants';
import { scale } from '@utils/normalize';
import { getAmplitudeKeyValue, showErrorOnAPI } from '@utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BackHandler, Modal, TouchableOpacity } from 'react-native';
import { logConfirmWithdrawalPressed, logTnCClosedForWithdrawal, logTnCOpen } from './analyticsWithdrawDetails';
import styles from './styles';

interface GGWithdrawDetailsProps {
  route: {
    params: {
      source: string;
      transaction_category_id: string;
      withdrawalData: {
        transactionId: string;
        requestedAmount: number;
        fee: number;
        transferAmount: number;
        bank: {
          bankName: string;
          accountNumber: string;
        };
      };
      fromTransfer: boolean;
    };
  };
}

function GGWithdrawDetails({ route }: GGWithdrawDetailsProps): React.ReactElement {
  const navigation = useNavigation();
  const { t } = useTranslation('common');
  const { params } = route;
  const { withdrawalData, fromTransfer, source, transaction_category_id } = params;
  const toast = useToast();
  const [tncChecked, setTncChecked] = useState(false);
  const [tncOpen, setTncOpen] = useState(false);
  const [showReasonSelector, setShowReasonSelector] = useState(false);
  const queryClient = useQueryClient();
  // Remove ref - control with index prop

  const {
    withdrawalReason,
    setLoading,
    setInitialData,
    setWithdrawalReason,
    selectedAccount,
    setReferralValidated,
    setValidatedReferralCode,
    validatedReferralCode,
    meAmplitudeContext,
  } = getStore();

  const { amplitudeAccountName } = selectedAccount;

  const {
    isLoading,
    isSuccess,
    isError,
    error,
    data,
    mutate: confirmWithdrawal,
  } = useMutation({
    mutationFn: () => apiWithdrawalConfirm({ transactionId: withdrawalData.transactionId }),
  });

  useEffect(() => {
    logPaymentSummarySuccessfull(
      transaction_category_id,
      withdrawalData,
      withdrawalReason,
      amplitudeAccountName,
      validatedReferralCode,
      source,
    );
    logScreenLaunch(
      withdrawalData,
      withdrawalReason,
      amplitudeAccountName,
      validatedReferralCode,
      source,
      transaction_category_id,
    );

    const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
      logScreenBack(transaction_category_id, amplitudeAccountName, source, validatedReferralCode);
      return false;
    });

    return () => backHandler.remove();
  }, []);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    showErrorOnAPI(isError, error, toast, t, navigation, setInitialData);
  }, [isError, error]);

  useEffect(() => {
    if (isSuccess && data) {
      queryClient.invalidateQueries({ queryKey: ['transactions_history'] });
      setValidatedReferralCode(null);
      setReferralValidated(false);
      navigation.replace(navigationConstants.GG_WITHDRAW_SUCCESS, {
        wdConfirm: data,
        withdrawalData,
        fromTransfer,
        withdrawalReason,
        validatedReferralCode,
        source,
        transaction_category_id,
        logParams: getAmplitudeKeyValue(
          AMPLITUDE_CONSTANTS.Withdrawal.transactionSuccess.name,
          data?.amplitudeEventContext,
        ),
      });
    }
  }, [isSuccess, data]);

  useEffect(() => {
    logTnCOpen(tncOpen, transaction_category_id, amplitudeAccountName, validatedReferralCode, source);
  }, [tncOpen]);

  const closeWithdrawalModal = () => {
    logTnCClosedForWithdrawal(transaction_category_id, source, amplitudeAccountName);
    setTncOpen(false);
  };

  // Remove useEffect - control with index prop

  const btnConfirmWithdrawalPressed = () => {
    if (!isLoading) {
      logConfirmWithdrawalPressed(
        transaction_category_id,
        withdrawalData,
        withdrawalReason,
        source,
        amplitudeAccountName,
        validatedReferralCode,
        meAmplitudeContext,
      );
      confirmWithdrawal();
    }
  };
  return (
    <View style={appStyles.safeAreaView}>
      <View style={appStyles.marginHorizontal(16)}>
        <Text testID="lblWithdrawHeader" variant="lg-bold" style={appStyles.marginTop(16)}>
          {t('withdrawalPage.detailsHeader')}
        </Text>
        <View style={appStyles.marginVertical(8)}>
          <WithdrawalAmount requestedAmount={withdrawalData.requestedAmount} />
          <WithdrawalAdminFee fee={withdrawalData.fee} />
          <Divider color={colors.neutral.mist} height="2px" style={appStyles.marginVertical(12)} />
          <WithdrawalTotal transferAmount={withdrawalData.transferAmount} />
        </View>
      </View>
      <Divider color={colors.neutral.mist} height="4px" style={appStyles.marginVertical(12)} />
      <View style={[appStyles.marginHorizontal(16), appStyles.flex1]}>
        <Text testID="lblBankHead" variant="lg-bold">
          {t('withdrawalPage.destinationAccount')}
        </Text>
        <WithdrawalBankDetails bank={withdrawalData.bank} />
        <WithdrawalReason
          showReasonSelector={showReasonSelector}
          setShowReasonSelector={setShowReasonSelector}
          reason={withdrawalReason?.reason}
          withdrawAmount={withdrawalData.requestedAmount}
          withdrawalReason={withdrawalReason}
          setWithdrawalReason={setWithdrawalReason}
          selectedAccount={selectedAccount}
          fromTransfer={fromTransfer}
          source={source}
          transaction_category_id={transaction_category_id}
        />
        <AppliedReferral />
        <BPTncCheckBox
          tncChecked={tncChecked}
          setTncOpen={setTncOpen}
          setTncChecked={setTncChecked}
          amplitudeAccountName={amplitudeAccountName}
          transaction_category_id={transaction_category_id}
          source={source}
        />
        <Modal
          visible={tncOpen}
          animationType="none"
          presentationStyle="overFullScreen"
          transparent
          onRequestClose={closeWithdrawalModal}>
          <TouchableOpacity style={styles.modalBackdrop} activeOpacity={1} onPress={closeWithdrawalModal}>
            <TouchableOpacity style={styles.modalContent} activeOpacity={1} onPress={e => e.stopPropagation()}>
              <View style={styles.modalHeaderContainer}>
                <View style={styles.modalHeaderRow}>
                  <BPTnCHeader />
                  <TouchableOpacity onPress={closeWithdrawalModal} style={styles.modalCloseButton}>
                    <AntDesign name="close" size={20} color={colors.neutral.charcoal} />
                  </TouchableOpacity>
                </View>
              </View>
              <View>
                <BPTnc
                  setTncChecked={setTncChecked}
                  setTncOpen={setTncOpen}
                  fromWithdrawal
                  transactionCategoryId={transaction_category_id}
                  accountName={amplitudeAccountName}
                />
              </View>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>
      </View>
      <View style={[appStyles.borderWithShadow, styles.footerView]}>
        <Button
          size="lg"
          testID="btnConfirmWithdrawal"
          isLoading={isLoading}
          style={[appStyles.marginBottom(16), { paddingVertical: scale(8) }]}
          onPress={btnConfirmWithdrawalPressed}
          isDisabled={!tncChecked}>
          {t('common.confirmed')}
        </Button>
      </View>
    </View>
  );
}

export default GGWithdrawDetails;
